import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import UserTable from "../components/UserTable";
import ChangePasswordForm from "../components/ChangePasswordForm";

import Button from "../../../components/common/Button";
import Input from "../../../components/common/Input";
import Modal from "../../../components/common/Modal";
import ErrorMessage from "../../../components/common/ErrorMessage";

import { useUsers } from "../hooks/useUsers";

const UsersPage = () => {
  const navigate = useNavigate();

  const {
    users,
    loading,
    error,
    changePassword,
    deleteUser
  } = useUsers();

  const [search, setSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPasswordModal, setShowPasswordModal] =
    useState(false);

  const [passwordLoading, setPasswordLoading] =
    useState(false);

  const [passwordError, setPasswordError] =
    useState("");

  const [deleteLoading, setDeleteLoading] =
    useState(false);

  const filteredUsers = useMemo(() => {
    const value = search.trim().toLowerCase();

    if (!value) {
      return users;
    }

    return users.filter((user) => {
      const name =
        user.name?.toLowerCase() || "";

      const email =
        user.email?.toLowerCase() || "";

      const phone =
        user.phone?.toLowerCase() || "";

      const roles =
        user.roles
          ?.join(" ")
          .toLowerCase() || "";

      return (
        name.includes(value) ||
        email.includes(value) ||
        phone.includes(value) ||
        roles.includes(value)
      );
    });
  }, [users, search]);

  const handleCreate = () => {
    navigate("/users/create");
  };

  const handleEdit = (user) => {
    navigate(`/users/${user.id}/edit`);
  };

  const handleView = (user) => {
    navigate(`/users/${user.id}`);
  };

  const handleChangePassword = (user) => {
    setSelectedUser(user);
    setPasswordError("");
    setShowPasswordModal(true);
  };

  const handlePasswordSubmit = async ({
    newPassword
  }) => {
    if (!selectedUser) {
      return;
    }

    try {
      setPasswordLoading(true);
      setPasswordError("");

      await changePassword(
        selectedUser.id,
        newPassword
      );

      setShowPasswordModal(false);
      setSelectedUser(null);
    } catch (error) {
      setPasswordError(
        error?.response?.data?.message ||
          "Unable to change password."
      );
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleDelete = async (user) => {
    const confirmed = window.confirm(
      `Are you sure you want to deactivate ${user.name}?`
    );

    if (!confirmed) {
      return;
    }

    try {
      setDeleteLoading(true);

      await deleteUser(user.id);
    } catch (error) {
      window.alert(
        error?.response?.data?.message ||
          "Unable to deactivate user."
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  const totalUsers = users.length;

  const activeUsers = users.filter(
    (user) => user.status === "ACTIVE"
  ).length;

  const inactiveUsers = users.filter(
    (user) => user.status === "INACTIVE"
  ).length;

  return (
    <>
      <div className="users-page">
        {/* PAGE HEADER */}
        <div className="users-page-header">
          <div>
            <h1 className="page-title">
              Users
            </h1>

            <p className="page-description">
              Manage your sales and agent accounts.
            </p>
          </div>

          <Button
            onClick={handleCreate}
          >
            + Create user
          </Button>
        </div>

        {/* ERROR */}
        {error && (
          <ErrorMessage
            message={
              typeof error === "string"
                ? error
                : "Unable to load users."
            }
          />
        )}

        {/* SEARCH */}
        <div className="users-search-card">
          <Input
            label="Search users"
            placeholder="Search by name, email, phone or role..."
            value={search}
            onChange={(event) =>
              setSearch(event.target.value)
            }
          />
        </div>

        {/* SUMMARY */}
        <div className="users-summary-grid">
          <div className="summary-card">
            <div className="summary-card-content">
              <div>
                <p className="summary-label">
                  Total users
                </p>

                <p className="summary-value">
                  {totalUsers}
                </p>
              </div>

              <div className="summary-icon summary-icon-blue">
                Users
              </div>
            </div>
          </div>

          <div className="summary-card">
            <div className="summary-card-content">
              <div>
                <p className="summary-label">
                  Active users
                </p>

                <p className="summary-value summary-value-success">
                  {activeUsers}
                </p>
              </div>

              <div className="summary-icon summary-icon-green">
                ✓
              </div>
            </div>
          </div>

          <div className="summary-card">
            <div className="summary-card-content">
              <div>
                <p className="summary-label">
                  Inactive users
                </p>

                <p className="summary-value summary-value-muted">
                  {inactiveUsers}
                </p>
              </div>

              <div className="summary-icon summary-icon-gray">
                —
              </div>
            </div>
          </div>
        </div>

        {/* USER TABLE */}
        <UserTable
          users={filteredUsers}
          loading={loading || deleteLoading}
          onView={handleView}
          onEdit={handleEdit}
          onChangePassword={handleChangePassword}
          onDelete={handleDelete}
        />
      </div>

      {/* CHANGE PASSWORD MODAL */}
      <Modal
        isOpen={showPasswordModal}
        onClose={() => {
          if (!passwordLoading) {
            setShowPasswordModal(false);
            setSelectedUser(null);
          }
        }}
        title="Change password"
      >
        <div className="password-modal-description">
          <p>
            Change password for{" "}
            <span>
              {selectedUser?.name}
            </span>
            .
          </p>
        </div>

        <ChangePasswordForm
          onSubmit={handlePasswordSubmit}
          onCancel={() => {
            if (!passwordLoading) {
              setShowPasswordModal(false);
              setSelectedUser(null);
            }
          }}
          loading={passwordLoading}
          serverError={passwordError}
        />
      </Modal>
    </>
  );
};

export default UsersPage;